import { makeStyles } from "@material-ui/core";
import * as React from "react";

import { PLANNER_EASING, PLANNER_MOTION } from "./plannerMotion";

const useStyles = makeStyles(() => ({
  "@keyframes enter": {
    from: { opacity: 0, transform: "translateY(var(--planner-offset))" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  root: {
    "&[data-planner-motion='enter']": { animation: "$enter var(--planner-duration) var(--planner-easing) var(--planner-delay) both" },
    "&[data-planner-motion='pending']": { opacity: 0, transform: "translateY(var(--planner-offset))" },
    "&:focus-within": { animation: "none", opacity: 1, transform: "none" },
    "@media (prefers-reduced-motion: reduce), print": {
      "&&": { animation: "none", opacity: 1, transform: "none" },
    },
  },
}));

export function usePlannerReducedMotion() {
  const [reduced, setReduced] = React.useState(
    () => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  React.useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(media.matches);
    if (media.addEventListener) media.addEventListener("change", update);
    else media.addListener(update);
    return () => {
      if (media.removeEventListener) media.removeEventListener("change", update);
      else media.removeListener(update);
    };
  }, []);
  return reduced;
}

export function usePlannerActionReady(delay: number, reduced: boolean) {
  const [ready, setReady] = React.useState(reduced);
  React.useEffect(() => {
    if (reduced) return;
    const timer = window.setTimeout(() => setReady(true), delay);
    return () => window.clearTimeout(timer);
  }, [delay, reduced]);
  return reduced || ready;
}

export function usePlannerSampleScore(reduced: boolean) {
  const [score, setScore] = React.useState(reduced ? 4.8 : 0);
  React.useEffect(() => {
    if (reduced) return;
    let frame = 0;
    const timer = window.setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        const progress = Math.min(1, (now - start) / PLANNER_MOTION.scoreDuration);
        setScore(4.8 * (1 - Math.pow(1 - progress, 3)));
        if (progress < 1) frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    }, PLANNER_MOTION.score);
    return () => {
      window.clearTimeout(timer);
      window.cancelAnimationFrame(frame);
    };
  }, [reduced]);
  return reduced ? 4.8 : score;
}

/** 登場のフェードと、スクロールで初めて見えたときの演出。 */
export const PlannerReveal: React.FC<{
  delay?: number;
  duration?: number;
  animate?: boolean;
  onScroll?: boolean;
  className?: string;
}> = ({ children, delay = 0, duration = PLANNER_MOTION.introReveal, animate = true, onScroll = false, className = "" }) => {
  const classes = useStyles();
  const ref = React.useRef<HTMLDivElement>(null);
  const observer = React.useRef<IntersectionObserver>();
  const [phase, setPhase] = React.useState(onScroll ? "static" : "enter");
  React.useLayoutEffect(() => {
    if (!animate || !onScroll || !ref.current || typeof IntersectionObserver === "undefined") return;
    let active = true;
    const instance = new IntersectionObserver((entries) => {
      if (!active || observer.current !== instance || !entries.some((entry) => entry.isIntersecting)) return;
      setPhase("enter");
      instance.disconnect();
      observer.current = undefined;
    }, { rootMargin: "0px 0px -24px 0px", threshold: 0.05 });
    observer.current = instance;
    setPhase("pending");
    instance.observe(ref.current);
    return () => {
      active = false;
      instance.disconnect();
    };
  }, [animate, onScroll]);
  return (
    <div
      ref={ref}
      className={`${classes.root} ${className}`}
      data-planner-motion={animate ? phase : "static"}
      style={{
        "--planner-delay": `${delay}ms`,
        "--planner-duration": `${duration}ms`,
        "--planner-offset": onScroll ? "20px" : "12px",
        "--planner-easing": onScroll ? "cubic-bezier(0.16, 1, 0.3, 1)" : PLANNER_EASING,
      } as React.CSSProperties}
      onFocusCapture={() => { observer.current?.disconnect(); observer.current = undefined; setPhase("static"); }}
    >
      {children}
    </div>
  );
};
